import React from "react";
import {
  DialogActions,
  DialogActionsProps,
  DialogContent,
  DialogContentProps,
  DialogProps,
  DialogTitle,
  DialogTitleProps,
  Dialog as MuiDialog,
} from "@mui/material";
import classNames from "classnames";

import { ReactComponent as CloseSVG } from "App/common/icons-v2/close.svg";
import { ReactComponent as InfoSVG } from "App/common/icons/info.svg";
import { ButtonV2 } from "App/core/ButtonV2";

import styles from "./DialogV2.module.scss";

type DialogHeaderV2Props = Omit<DialogTitleProps, "title"> & {
  title: React.ReactNode | string;
  superTitle?: string;
  description?: React.ReactNode | string;
  showCloseButton?: boolean;
  actionButton?: React.ReactNode | string;
  removeBottomBorder?: boolean;
  hideHeader?: boolean;
  onClose?: DialogProps["onClose"];
};

export const DialogHeaderV2 = ({
  className,
  children,
  description,
  title,
  superTitle,
  showCloseButton = true,
  actionButton,
  removeBottomBorder,
  hideHeader,
  ...props
}: DialogHeaderV2Props) => {
  if (hideHeader) {
    return null;
  }

  return (
    <div
      className={classNames(
        styles.headerRoot,
        {
          [styles.removeBottomBorder]: removeBottomBorder,
        },
        className,
      )}
    >
      {/* Header top row: title + actions */}
      <div className={styles.headerRow}>
        <div className={styles.titleBlock}>
          {superTitle && <div className={styles.superTitle}>{superTitle}</div>}
          <DialogTitle {...props} className={styles.title}>
            {title}
          </DialogTitle>
        </div>

        {(actionButton || showCloseButton) && (
          <div className={styles.actions}>
            {actionButton}
            {showCloseButton && (
              <ButtonV2
                color="ghost"
                size="sm"
                className={styles.closeButton}
                onClick={(e) => props.onClose?.(e, "escapeKeyDown")}
                aria-label="Close"
              >
                <CloseSVG />
              </ButtonV2>
            )}
          </div>
        )}
      </div>

      {/* Optional description */}
      {description && (
        <div className={styles.description} id="dialog-description">
          {description}
        </div>
      )}

      {/* Optional children */}
      {children}
    </div>
  );
};

type DialogContentV2Props = DialogContentProps & {
  removePadding?: boolean;
};

export const DialogContentV2 = ({
  className,
  removePadding,
  children,
  ...props
}: DialogContentV2Props) => {
  return (
    <DialogContent
      {...props}
      className={classNames(
        styles.content,
        {
          [styles.removePadding]: removePadding,
        },
        className,
      )}
    >
      {children}
    </DialogContent>
  );
};

type DialogFooterV2Props = DialogActionsProps;

export const DialogFooterV2 = ({ children, ...props }: DialogFooterV2Props) => {
  return (
    <DialogActions
      {...props}
      className={classNames(styles.footer, props.className)}
    >
      {children}
    </DialogActions>
  );
};

type DialogInfoProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>;

export const DialogInfoV2 = ({ children, ...props }: DialogInfoProps) => {
  return (
    <div className={styles.info} {...props}>
      <InfoSVG />
      {children}
    </div>
  );
};

type DialogRootV2Props = DialogProps;

export const DialogRootV2 = ({
  className,
  children,
  ...props
}: DialogRootV2Props) => {
  return (
    <MuiDialog
      {...props}
      data-cypress-id="highnote-dialog"
      className={classNames(styles.root, className)}
      classes={{
        ...props.classes,
        paperFullScreen: classNames(
          styles.paperFullScreen,
          props.classes?.paperFullScreen,
        ),
      }}
      BackdropProps={{
        ...props.BackdropProps,
        className: classNames(styles.backdrop, props.BackdropProps?.className),
      }}
      PaperProps={{
        ...props.PaperProps,
        className: classNames(styles.paper, props.PaperProps?.className),
      }}
    >
      {children}
    </MuiDialog>
  );
};

type FullyConfiguredDialogV2Props = DialogRootV2Props & {
  header?: Pick<
    DialogHeaderV2Props,
    | "title"
    | "superTitle"
    | "description"
    | "showCloseButton"
    | "actionButton"
    | "onClose"
    | "children"
    | "className"
    | "removeBottomBorder"
    | "hideHeader"
  >;
  footer?: Pick<DialogFooterV2Props, "children" | "className">;
  info?: Pick<DialogInfoProps, "children" | "className">;
  contentProps?: Omit<DialogContentV2Props, "children">;
  removeDefaultContentWrapper?: boolean;
};

/**
 * A fully configured dialog component that provides a complete, opinionated implementation
 * of a dialog with header, content, footer, and info sections.
 *
 * @description
 * This component provides a pre-configured dialog layout with all common dialog elements.
 * It's the recommended way to create dialogs when you need a standard dialog structure
 * with header, content, and optional footer/info sections.
 *
 * @example
 * ```tsx
 * <FullyConfiguredDialogV2
 *   open={isOpen}
 *   onClose={handleClose}
 *   header={{
 *     title: "Dialog Title",
 *     description: "Optional description"
 *   }}
 *   footer={{
 *     children: <Button onClick={handleClose}>Close</Button>
 *   }}
 * >
 *   Dialog content goes here
 * </FullyConfiguredDialogV2>
 * ```
 *
 * @alternative
 * For custom dialog layouts or more granular control, you can use the individual components:
 * ```tsx
 * <DialogRootV2>
 *   <DialogHeaderV2 title="Custom Header" />
 *   <DialogContentV2>Custom content</DialogContentV2>
 *   <DialogFooterV2>Custom footer</DialogFooterV2>
 * </DialogRootV2>
 * ```
 *
 * @props
 * - `header` - Configuration for the dialog header (title, description, etc.)
 * - `footer` - Configuration for the dialog footer
 * - `info` - Optional info section that appears below the content
 * - `contentProps` - Props to pass to the content section
 * - `removeDefaultContentWrapper` - If true, children will not be wrapped in the default DialogContentV2 component
 *
 * Plus all props from MUI's DialogProps
 */
export const FullyConfiguredDialogV2 = ({
  children,
  header,
  footer,
  info,
  removeDefaultContentWrapper,
  contentProps,
  ...props
}: FullyConfiguredDialogV2Props) => {
  return (
    <DialogRootV2 {...props}>
      {header && (
        <DialogHeaderV2 {...header} onClose={header.onClose || props.onClose} />
      )}
      {removeDefaultContentWrapper ? (
        children
      ) : (
        <DialogContentV2 {...contentProps}>{children}</DialogContentV2>
      )}
      {footer && <DialogFooterV2 {...footer} />}
      {info && <DialogInfoV2 {...info} />}
    </DialogRootV2>
  );
};
